import {
  CarClaimDescription,
  DrivingConvictionDescription,
  FinancialInstitution,
  FireHouseDistance,
  FireHydrantDistance,
  HomeClaimDescription,
  MortgageLenders,
  Relationship,
  YearlyDaysOutsideOfProvince,
} from '~domain/model/application/allset-data.type';
import { OccupationCategory } from '~domain/model/application/common/occupation';
import { PlumbingType, RoofType, StructureDamages } from '~domain/model/application/home/home-structure-condition';
import { NumberOfBathrooms } from '~domain/model/application/home/number-of-bathrooms';
import { Province } from '~domain/model/core/address/province';
import { Carrier } from '~domain/model/core/carrier';
import { Months } from '~domain/model/core/date/months';
import { FloaterType } from '~domain/model/quote/floater';
import { CarUninsuredReason, HomeUninsuredReason } from '../application/common/uninsured-reason';

export interface Suggestion<T = SuggestionValueType> {
  value: T;
  label?: string;
}

export type SuggestionValueType = string | number | boolean;

export interface SuggestionList {
  id: string;
  suggestions: Suggestion[];
}

export enum SuggestionType {
  CarClaimDescription = 'carClaimDescription',
  Carriers = 'carriers',
  DrivingConvictionDescription = 'drivingConvictionDescription',
  FinancialInstitution = 'financialInstitutions',
  FireHouseDistance = 'fireStationDistance',
  FireHydrantDistance = 'fireHydrantDistance',
  FloaterTypes = 'floaterTypes',
  HomeClaimDescription = 'homeClaimDescription',
  Months = 'months',
  MortgageLenders = 'mortgageLenders',
  NumberOfBathrooms = 'numberOfBathrooms',
  OccupationCategories = 'occupationCategories',
  PlumbingType = 'plumbingType',
  Province = 'province',
  Relationship = 'relationship',
  RoofType = 'roofType',
  StructureDamages = 'structureDamages',
  YearlyDaysOutsideOfProvince = 'yearlyDaysOutsideOfProvince',
  HomeUninsuredReason = 'homeUninsuredReason',
  CarUninsuredReason = 'carUninsuredReason',
}

export const SuggestionLists: SuggestionList[] = [
  {
    // Do not change the values. Each value is a specific code for a insurer.
    id: SuggestionType.Carriers,
    suggestions: [
      { value: Carrier.Allstate },
      { value: Carrier.Alpha },
      { value: Carrier.Aviva },
      { value: Carrier.AvivaElite },
      { value: Carrier.AvivaTraders },
      { value: Carrier.Bcaa },
      { value: Carrier.Belair },
      { value: Carrier.Beneva },
      { value: Carrier.Caa },
      { value: Carrier.Chieftain },
      { value: Carrier.Chubb },
      { value: Carrier.Coachman },
      { value: Carrier.Cooperators },
      { value: Carrier.Cumis },
      { value: Carrier.Desjardins },
      { value: Carrier.Dumfries },
      { value: Carrier.Echelon },
      { value: Carrier.Economical },
      { value: Carrier.Facility },
      { value: Carrier.Federation },
      { value: Carrier.GoreMutual },
      { value: Carrier.Guarantee },
      { value: Carrier.Ia },
      { value: Carrier.Intact },
      { value: Carrier.Intergroupe },
      { value: Carrier.Jevco },
      { value: Carrier.LaCapitale },
      { value: Carrier.LaPersonnelle },
      { value: Carrier.Ledor },
      { value: Carrier.Lloyds },
      { value: Carrier.Missiquoi },
      { value: Carrier.Northbridge },
      { value: Carrier.NationalBankIns },
      { value: Carrier.Optium },
      { value: Carrier.Other },
      { value: Carrier.Pafco },
      { value: Carrier.PeelMutual },
      { value: Carrier.Pembridge },
      { value: Carrier.Perth },
      { value: Carrier.Portage },
      { value: Carrier.Promutuel },
      { value: Carrier.Rbc },
      { value: Carrier.Rsa },
      { value: Carrier.Sonnet },
      { value: Carrier.SquareOne },
      { value: Carrier.Ssq },
      { value: Carrier.StateFarm },
      { value: Carrier.Td },
      { value: Carrier.Travelers },
      { value: Carrier.Unica },
      { value: Carrier.Unique },
      { value: Carrier.Wawanesa },
      { value: Carrier.Western },
    ].map((suggestion) => ({
      label: suggestion.value,
      value: suggestion.value,
    })),
  },
  {
    // Do not change the values. Each value is a specific code for a claim type.
    id: SuggestionType.CarClaimDescription,
    suggestions: [
      { value: CarClaimDescription.Collision },
      { value: CarClaimDescription.Fire },
      { value: CarClaimDescription.GlassBreakage },
      { value: CarClaimDescription.Hail },
      { value: CarClaimDescription.Liability },
      { value: CarClaimDescription.LiabilityCollision },
      { value: CarClaimDescription.Theft },
      { value: CarClaimDescription.Vandalism },
      { value: CarClaimDescription.OtherNonResponsible },
    ].map((suggestion) => ({
      label: suggestion.value,
      value: suggestion.value,
    })),
  },
  {
    id: SuggestionType.DrivingConvictionDescription,
    suggestions: [
      // Keep it in blocks to make it easier to compare with Fortus documentation
      // Severity minor (37)
      { value: DrivingConvictionDescription.CrowdingDriversSeat },
      { value: DrivingConvictionDescription.DefectiveBrakes },
      { value: DrivingConvictionDescription.DistractedDriving },
      { value: DrivingConvictionDescription.DriversLicenceViolations },
      { value: DrivingConvictionDescription.FailureToCarryInsuranceCard },
      { value: DrivingConvictionDescription.FailureToProduceEvidenceOfInsurance },
      { value: DrivingConvictionDescription.FailingToShareRoad },
      { value: DrivingConvictionDescription.FailingToSignal },
      { value: DrivingConvictionDescription.FailureToUseSeatbelts },
      { value: DrivingConvictionDescription.FailingToYield },
      { value: DrivingConvictionDescription.FailingToYieldPedestrian },
      { value: DrivingConvictionDescription.FollowingTooClosely },
      { value: DrivingConvictionDescription.HeadlightOffenses },
      { value: DrivingConvictionDescription.ImproperDrivingBusLane },
      { value: DrivingConvictionDescription.ImproperLaneChange },
      { value: DrivingConvictionDescription.ImproperOpeningDoor },
      { value: DrivingConvictionDescription.ImproperPassing },
      { value: DrivingConvictionDescription.ImproperTowing },
      { value: DrivingConvictionDescription.ImproperTurn },
      { value: DrivingConvictionDescription.ImproperDividedHighway },
      { value: DrivingConvictionDescription.InsecureLoad },
      { value: DrivingConvictionDescription.MinorConvictionNotSpecified },
      { value: DrivingConvictionDescription.ObstructingTraffic },
      { value: DrivingConvictionDescription.Overload },
      { value: DrivingConvictionDescription.ProhibitedHandHeldDevice },
      { value: DrivingConvictionDescription.RailwayCrossing },
      { value: DrivingConvictionDescription.Speeding },
      { value: DrivingConvictionDescription.StopSign },
      { value: DrivingConvictionDescription.TrafficLight },
      { value: DrivingConvictionDescription.TrailerPassenger },
      { value: DrivingConvictionDescription.UnnecessaryNoise },
      { value: DrivingConvictionDescription.UnnecessarySlowDriving },
      { value: DrivingConvictionDescription.UnsafeMove },
      { value: DrivingConvictionDescription.UnsafeTurn },
      { value: DrivingConvictionDescription.UnsafeVehicle },
      { value: DrivingConvictionDescription.ViewObstructed },
      { value: DrivingConvictionDescription.WrongWayOnOneWay },

      // Severity major (10)
      { value: DrivingConvictionDescription.FailingToReportAccident },
      { value: DrivingConvictionDescription.FailureToReportDamageHighway },
      { value: DrivingConvictionDescription.FalseStatementInsurance },
      { value: DrivingConvictionDescription.MajorConvictionNotSpecified },
      { value: DrivingConvictionDescription.OperateMotorVehicleWithoutInsurance },
      { value: DrivingConvictionDescription.ProduceFalseInsurance },
      { value: DrivingConvictionDescription.SchoolBusFailStop },
      { value: DrivingConvictionDescription.SchoolPlaygroundImproperPassing },
      { value: DrivingConvictionDescription.Stunting },
      { value: DrivingConvictionDescription.VehicleOwnerWithoutInsurance },

      // Severity criminal (13)
      { value: DrivingConvictionDescription.BloodAlcohol },
      { value: DrivingConvictionDescription.CarelessDriving },
      { value: DrivingConvictionDescription.CriminalNegligence },
      { value: DrivingConvictionDescription.DangerousDriving },
      { value: DrivingConvictionDescription.DrivingWhileUnderSuspension },
      { value: DrivingConvictionDescription.FailingToObeyPolice },
      { value: DrivingConvictionDescription.FailingToRemain },
      { value: DrivingConvictionDescription.ImpairedDriving },
      { value: DrivingConvictionDescription.MotorManslaughter },
      { value: DrivingConvictionDescription.Racing },
      { value: DrivingConvictionDescription.RefuseBreathalyzer },
      { value: DrivingConvictionDescription.SeriousConvictionNotSpecified },
    ].map((suggestion) => ({
      label: suggestion.value,
      value: suggestion.value,
    })),
  },
  {
    id: SuggestionType.FinancialInstitution,
    suggestions: [
      { label: FinancialInstitution.BankofMontreal, value: '001' },
      { label: FinancialInstitution.Scotiabank, value: '002' },
      { label: FinancialInstitution.RoyalBank, value: '003' },
      { label: FinancialInstitution.TdBank, value: '004' },
      { label: FinancialInstitution.NationalBank, value: '006' },
      { label: FinancialInstitution.Cibc, value: '010' },
      { label: FinancialInstitution.HsbcBank, value: '016' },
      { label: FinancialInstitution.CanadianWesternBank, value: '030' },
      { label: FinancialInstitution.LaurentianBank, value: '039' },
      { label: FinancialInstitution.PresidentChoiceBank, value: '320' },
      { label: FinancialInstitution.ManulifeBank, value: '540' },
      { label: FinancialInstitution.AlternaBank, value: '608' },
      { label: FinancialInstitution.TangerineBank, value: '614' },
      { label: FinancialInstitution.PeoplesTrustBank, value: '621' },
      { label: FinancialInstitution.EquitableBank, value: '623' },
      { label: FinancialInstitution.VancityBank, value: '809' },
      { label: FinancialInstitution.DesjardinsGroup, value: '815' },
      { label: FinancialInstitution.Central1CreditUnion, value: '828' },
      { label: FinancialInstitution.Motusbank, value: '374' },
      { label: FinancialInstitution.WealthOneBank, value: '370' },
      { label: FinancialInstitution.DesjardinsOntarioCreditUnion, value: '829' },
      { label: FinancialInstitution.AlternaSavings, value: '842' },
      { label: FinancialInstitution.MeridianCreditUnion, value: '837' },
      { label: FinancialInstitution.Wealthsimple, value: '703' },
      { label: FinancialInstitution.CaisseAlliance, value: '890' },
    ].map((suggestion) => ({
      label: suggestion.label,
      value: suggestion.value,
    })),
  },
  {
    id: SuggestionType.FireHouseDistance,
    suggestions: [
      { label: FireHouseDistance.Within8km, value: 8000 },
      { label: FireHouseDistance.Between8and13km, value: 13000 },
      { label: FireHouseDistance.Over13km, value: 13001 },
    ].map((suggestion) => ({
      label: suggestion.label,
      value: suggestion.value,
    })),
  },
  {
    // Define which floater a user can select
    id: SuggestionType.FloaterTypes,
    suggestions: Object.keys(FloaterType).map((key) => ({
      label: FloaterType[key],
      value: FloaterType[key],
    })),
  },
  {
    // Do not change the values. Each value is a specific code for a claim type.
    id: SuggestionType.HomeClaimDescription,
    suggestions: [
      { value: HomeClaimDescription.AirConditioner },
      { value: HomeClaimDescription.BodilyInjury },
      { value: HomeClaimDescription.Burglary },
      { value: HomeClaimDescription.CardFraud },
      { value: HomeClaimDescription.Earthquake },
      { value: HomeClaimDescription.Explosion },
      { value: HomeClaimDescription.ExteriorWaterLine },
      { value: HomeClaimDescription.ExteriorSewerLine },
      { value: HomeClaimDescription.Fire },
      { value: HomeClaimDescription.FloodWater },
      { value: HomeClaimDescription.GroundWater },
      { value: HomeClaimDescription.GlassBreakage },
      { value: HomeClaimDescription.IdentityTheft },
      { value: HomeClaimDescription.Other },
      { value: HomeClaimDescription.PersonalPropertyDamage },
      { value: HomeClaimDescription.SewerBackup },
      { value: HomeClaimDescription.Vandalism },
      { value: HomeClaimDescription.WaterGround },
      { value: HomeClaimDescription.Windstorm },
    ].map((suggestion) => ({
      label: suggestion.value,
      value: suggestion.value,
    })),
  },
  {
    id: SuggestionType.Months,
    suggestions: [
      {
        label: Months.January,
        value: 1,
      },
      {
        label: Months.February,
        value: 2,
      },
      {
        label: Months.March,
        value: 3,
      },
      {
        label: Months.April,
        value: 4,
      },
      {
        label: Months.May,
        value: 5,
      },
      {
        label: Months.June,
        value: 6,
      },
      {
        label: Months.July,
        value: 7,
      },
      {
        label: Months.August,
        value: 8,
      },
      {
        label: Months.September,
        value: 9,
      },
      {
        label: Months.October,
        value: 10,
      },
      {
        label: Months.November,
        value: 11,
      },
      {
        label: Months.December,
        value: 12,
      },
    ],
  },
  {
    // Used suggestion to have the option to change the displayed text to string - if needed
    id: SuggestionType.NumberOfBathrooms,
    suggestions: [
      {
        label: NumberOfBathrooms.One,
        value: 1,
      },
      {
        label: NumberOfBathrooms.OneFullAndOneHalf,
        value: 1.5,
      },
      {
        label: NumberOfBathrooms.Two,
        value: 2,
      },
      {
        label: NumberOfBathrooms.TwoFullAndOneHalf,
        value: 2.5,
      },
      {
        label: NumberOfBathrooms.Three,
        value: 3,
      },
      {
        label: NumberOfBathrooms.ThreeFullAndOneHalf,
        value: 3.5,
      },
      {
        label: NumberOfBathrooms.Four,
        value: 4,
      },
      {
        label: NumberOfBathrooms.FourFullAndOneHalf,
        value: 4.5,
      },
      {
        label: NumberOfBathrooms.Five,
        value: 5,
      },
      {
        label: NumberOfBathrooms.FiveFullAndOneHalf,
        value: 5.5,
      },
      {
        label: NumberOfBathrooms.Six,
        value: 6,
      },
      {
        label: NumberOfBathrooms.SixFullAndOneHalf,
        value: 6.5,
      },
    ],
  },
  {
    id: SuggestionType.OccupationCategories,
    suggestions: Object.keys(OccupationCategory).map((key: string) => ({
      label: OccupationCategory[key],
      value: OccupationCategory[key],
    })),
  },
  {
    id: SuggestionType.MortgageLenders,
    suggestions: [
      { value: MortgageLenders.CibcBank },
      { value: MortgageLenders.CanadaDevelopmentBank },
      { value: MortgageLenders.HSBCBank },
      { value: MortgageLenders.LaurentianBank },
      { value: MortgageLenders.ManulifeBank },
      { value: MortgageLenders.NationalBank },
      { value: MortgageLenders.RoyalBank },
      { value: MortgageLenders.ScotiaBank },
      { value: MortgageLenders.DesjardinsGroup },
      { value: MortgageLenders.CitiBank },
      { value: MortgageLenders.ComputershareTrustCompany },
      { value: MortgageLenders.FirstNational },
      { value: MortgageLenders.InvestorsGroup },
      { value: MortgageLenders.IndustrialAlliance },
      { value: MortgageLenders.IngBank },
      { value: MortgageLenders.QuebecInvestment },
      { value: MortgageLenders.JohnDeere },
      { value: MortgageLenders.Kubota },
      { value: MortgageLenders.Capital },
      { value: MortgageLenders.MontrealTrustCompany },
      { value: MortgageLenders.PeoplesTrustCompany },
      { value: MortgageLenders.SCHL },
      { value: MortgageLenders.StandardLife },
      { value: MortgageLenders.TangerineBank },
      { value: MortgageLenders.TdBank },
    ].map((suggestion) => ({
      label: suggestion.value,
      value: suggestion.value,
    })),
  },
  {
    id: SuggestionType.PlumbingType,
    suggestions: Object.keys(PlumbingType).map((key: string) => ({
      label: PlumbingType[key],
      value: PlumbingType[key],
    })),
  },
  {
    // Do not change the values. Each value is a specific code for a claim type.
    id: SuggestionType.Province,
    suggestions: [
      { value: Province.Alberta },
      { value: Province.BritishColumbia },
      { value: Province.Manitoba },
      { value: Province.Newfoundland },
      { value: Province.NovaScotia },
      { value: Province.NewBrunswick },
      { value: Province.Ontario },
      { value: Province.PriceEdwardIsland },
      { value: Province.Quebec },
      { value: Province.Saskatchewan },
    ].map((suggestion) => ({
      label: suggestion.value,
      value: suggestion.value,
    })),
  },
  {
    id: SuggestionType.Relationship,
    suggestions: [
      { value: Relationship.BoyfriendOrGirlfriend },
      { value: Relationship.Family },
      { value: Relationship.MarriedOrCivilUnion },
      { value: Relationship.Other },
      { value: Relationship.Roomate },
    ].map((suggestion) => ({
      label: suggestion.value,
      value: suggestion.value,
    })),
  },
  {
    id: SuggestionType.RoofType,
    suggestions: Object.keys(RoofType).map((key) => ({ label: RoofType[key], value: RoofType[key] })),
  },
  {
    id: SuggestionType.StructureDamages,
    suggestions: Object.keys(StructureDamages).map((key) => ({
      label: StructureDamages[key],
      value: StructureDamages[key],
    })),
  },
  {
    id: SuggestionType.FireHydrantDistance,
    suggestions: [
      { label: FireHydrantDistance.Over300m, value: 301 },
      { label: FireHydrantDistance.Within300m, value: 300 },
    ].map((suggestion) => ({
      label: suggestion.label,
      value: suggestion.value,
    })),
  },
  {
    id: SuggestionType.YearlyDaysOutsideOfProvince,
    suggestions: [
      { label: YearlyDaysOutsideOfProvince.Never, value: 0 },
      { label: YearlyDaysOutsideOfProvince.TwentyOne, value: 21 },
      { label: YearlyDaysOutsideOfProvince.FortyNine, value: 49 },
      { label: YearlyDaysOutsideOfProvince.OneHundredFive, value: 105 },
      { label: YearlyDaysOutsideOfProvince.OneHundredEightyTwo, value: 182 },
      { label: YearlyDaysOutsideOfProvince.OneHundredEightyNine, value: 189 },
    ],
  },
  {
    id: SuggestionType.HomeUninsuredReason,
    suggestions: Object.keys(HomeUninsuredReason).map((key) => ({
      label: HomeUninsuredReason[key],
      value: HomeUninsuredReason[key],
    })),
  },
  {
    id: SuggestionType.CarUninsuredReason,
    suggestions: Object.keys(CarUninsuredReason).map((key) => ({
      label: CarUninsuredReason[key],
      value: CarUninsuredReason[key],
    })),
  },
];
